/** Muss mit backend/app/services/firewall_formats.py übereinstimmen. */

export const FORTINET_POLICY_KEYS = [
  'Policy',
  'Source',
  'Destination',
  'Schedule',
  'Service',
  'Action',
  'IP Pool',
  'NAT',
  'Type',
  'Security Profiles',
  'Log',
  'Bytes',
  'Interface Pair',
] as const;

export const FORTINET_ADDRESS_KEYS = ['Name', 'Type', 'Interface', 'Details', 'IP', 'Ref.'] as const;
export const FORTINET_ADDRESS_GROUP_KEYS = ['Name', 'Type', 'Members', 'Ref.'] as const;
export const FORTINET_SERVICE_KEYS = ['Name', 'Details', 'IP/FQDN', 'Category', 'Protocol', 'Ref.'] as const;
export const FORTINET_VIP_KEYS = ['Name', 'Interface', 'Mapped From', 'Mapped To', 'Hit Count', 'Ref.'] as const;
export const FORTINET_VIP_GROUP_KEYS = ['Name', 'Members', 'Interface', 'Ref.'] as const;
export const FORTINET_IP_POOL_KEYS = ['Name', 'External IP Range', 'Ref.', 'Type', 'ARP Reply'] as const;
const POLICY_CSV_KEYS = FORTINET_POLICY_KEYS.filter((key) => key !== 'Interface Pair');
const POLICY_OPTIONAL_KEYS = ['Comment', 'Comments', 'Status'];

export type FirewallFormatId =
  | 'fortinet_policy_list'
  | 'fortinet_address_list'
  | 'fortinet_address_group'
  | 'fortinet_service_list'
  | 'fortinet_service_group'
  | 'fortinet_vip_list'
  | 'fortinet_vip_group'
  | 'fortinet_ip_pool'
  | 'fortinet_interface_list'
  | 'fortios_native'
  | 'sophos'
  | 'barracuda'
  | 'watchguard'
  | 'unknown';

export type FirewallUploadTyp =
  | 'policies'
  | 'adressen'
  | 'adressgruppen'
  | 'services'
  | 'servicegruppen'
  | 'vips'
  | 'vipgruppen'
  | 'ippools'
  | 'interfaces';

const LABELS: Record<FirewallFormatId, string> = {
  fortinet_policy_list: 'Fortinet policyList',
  fortinet_address_list: 'Fortinet Adressen',
  fortinet_address_group: 'Fortinet Adressgruppen',
  fortinet_service_list: 'Fortinet Dienste',
  fortinet_service_group: 'Fortinet Dienstgruppen',
  fortinet_vip_list: 'Fortinet Virtual IPs',
  fortinet_vip_group: 'Fortinet VIP-Gruppen',
  fortinet_ip_pool: 'Fortinet IP-Pools',
  fortinet_interface_list: 'Fortinet Interfaces',
  fortios_native: 'FortiOS',
  sophos: 'Sophos',
  barracuda: 'Barracuda',
  watchguard: 'WatchGuard',
  unknown: 'unbekanntes Format',
};

export const TYP_LABELS: Record<FirewallUploadTyp, string> = {
  policies: 'Policies',
  adressen: 'Adressen',
  adressgruppen: 'Adressgruppen',
  services: 'Dienste',
  servicegruppen: 'Dienstgruppen',
  vips: 'Virtual IPs',
  vipgruppen: 'VIP-Gruppen',
  ippools: 'IP-Pools',
  interfaces: 'Interfaces',
};

export const UPLOAD_TYPEN: FirewallUploadTyp[] = [
  'policies',
  'adressen',
  'adressgruppen',
  'services',
  'servicegruppen',
  'vips',
  'vipgruppen',
  'ippools',
  'interfaces',
];

export function formatLabel(formatId: string): string {
  return LABELS[formatId as FirewallFormatId] ?? LABELS.unknown;
}

export function uploadTyp(formatId: string): FirewallUploadTyp {
  switch (formatId) {
    case 'fortinet_address_list':
      return 'adressen';
    case 'fortinet_address_group':
      return 'adressgruppen';
    case 'fortinet_service_list':
      return 'services';
    case 'fortinet_service_group':
      return 'servicegruppen';
    case 'fortinet_vip_list':
      return 'vips';
    case 'fortinet_vip_group':
      return 'vipgruppen';
    case 'fortinet_ip_pool':
      return 'ippools';
    case 'fortinet_interface_list':
      return 'interfaces';
    default:
      return 'policies';
  }
}

export function isFirewallAudit(
  types: { name?: string | null; kuerzel?: string | null }[] | null | undefined,
): boolean {
  return (types ?? []).some(
    (type) => type?.name === 'Firewall Audit' || type?.kuerzel === 'FW',
  );
}

function hasKeys(value: unknown, expected: readonly string[]): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const keys = Object.keys(value as object);
  if (keys.length !== expected.length) return false;
  const present = new Set(keys);
  return expected.every((key) => present.has(key));
}

/** Policy-Liste: die festen Spalten, dazu optional Kommentar und Status. */
function hasPolicyKeys(value: unknown, required: readonly string[]): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const keys = Object.keys(value as object);
  const present = new Set(keys);
  if (!required.every((key) => present.has(key))) return false;
  const allowed = new Set<string>([...required, ...POLICY_OPTIONAL_KEYS]);
  return keys.every((key) => allowed.has(key));
}

function matchesPolicyHeader(present: Set<string>): boolean {
  const base = new Set(Array.from(present).filter((key) => !POLICY_OPTIONAL_KEYS.includes(key)));
  const same = (expected: readonly string[]) => expected.length === base.size && expected.every((key) => base.has(key));
  return same(FORTINET_POLICY_KEYS) || same(POLICY_CSV_KEYS);
}

function parseInterfaceHeader(text: string): string[] | null {
  const firstLine = text.replace(/^\uFEFF/, '').split(/\r?\n/).find((line) => line.trim().length > 0);
  if (!firstLine) return null;
  const header = firstLine.split('\t').map((cell) => cell.trim());
  if (!header.includes('Name') || !header.includes('Virtual Domain') || !header.includes('IP/Netmask')) return null;
  return header;
}

function groupFormat(rows: object[], filename: string): FirewallFormatId {
  const types = rows.map((row) => String((row as { Type?: unknown }).Type ?? '').trim().toLowerCase());
  if (types.some((typ) => typ.includes('firewall'))) return 'fortinet_service_group';
  if (types.some((typ) => typ === 'group')) return 'fortinet_address_group';
  return /service/i.test(filename) ? 'fortinet_service_group' : 'fortinet_address_group';
}

function katalogFormat(rows: object[], filename: string): FirewallFormatId | null {
  if (rows.every((row) => hasKeys(row, FORTINET_SERVICE_KEYS))) return 'fortinet_service_list';
  if (rows.every((row) => hasKeys(row, FORTINET_VIP_KEYS))) return 'fortinet_vip_list';
  if (rows.every((row) => hasKeys(row, FORTINET_VIP_GROUP_KEYS))) return 'fortinet_vip_group';
  if (rows.every((row) => hasKeys(row, FORTINET_IP_POOL_KEYS))) return 'fortinet_ip_pool';
  if (rows.every((row) => hasKeys(row, FORTINET_ADDRESS_GROUP_KEYS))) return groupFormat(rows, filename);
  return null;
}

function csvFormat(text: string, filename: string): FirewallFormatId | null {
  const headerLine = text.replace(/^\uFEFF/, '').split(/\r?\n/).find((line) => line.trim().length > 0);
  if (!headerLine || !headerLine.includes(',')) return null;
  const header = headerLine.split(',').map((cell) => cell.trim().replace(/^"|"$/g, ''));
  const present = new Set(header);
  if (header.length !== present.size) return null;
  const matches = (expected: readonly string[]) => expected.length === present.size && expected.every((key) => present.has(key));
  if (matchesPolicyHeader(present)) return 'fortinet_policy_list';
  if (matches(FORTINET_ADDRESS_KEYS)) return 'fortinet_address_list';
  const typeValue = /firewall group/i.test(text) ? 'Firewall Group' : 'Group';
  const fake = [Object.fromEntries(header.map((key) => [key, key === 'Type' ? typeValue : 'x']))];
  return katalogFormat(fake, filename);
}

export function detectFirewallText(text: string, filename = ''): FirewallFormatId {
  const trimmed = text.replace(/^\uFEFF/, '').trimStart();
  if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
    try {
      const data = JSON.parse(text.replace(/^\uFEFF/, ''));
      const rows = Array.isArray(data) ? data : [data];
      if (rows.length > 0 && rows.every((row) => row && typeof row === 'object' && !Array.isArray(row))) {
        if (rows.every((row) => hasPolicyKeys(row, FORTINET_POLICY_KEYS))) return 'fortinet_policy_list';
        if (rows.every((row) => hasKeys(row, FORTINET_ADDRESS_KEYS))) return 'fortinet_address_list';
        const katalog = katalogFormat(rows, filename);
        if (katalog) return katalog;
      }
    } catch {
      // kein vollständiges JSON, weitere Plugins prüfen
    }
  }
  if (parseInterfaceHeader(text)) return 'fortinet_interface_list';
  const csv = csvFormat(text, filename);
  if (csv) return csv;
  const head = trimmed.slice(0, 8000).toLowerCase();
  if (head.startsWith('#config-version=') || head.includes('config firewall policy')) {
    return 'fortios_native';
  }
  if (head.includes('"policyid"') && (head.includes('"srcintf"') || head.includes('"dstintf"'))) {
    return 'fortios_native';
  }
  if (head.includes('sophos')) return 'sophos';
  if (head.includes('barracuda')) return 'barracuda';
  if (head.includes('watchguard')) return 'watchguard';
  return 'unknown';
}

const POLICY_FULL = /^policyList-(.+?)-(policy-.+?)(?:_\d{4}-\d{2}-\d{2}(?:_[^.]+)?)?\.(?:json|csv)$/i;
const POLICY_SHORT = /^policyList-([^.]+)\.(?:json|csv)$/i;
const ADDRESS_NAME = /^Add?resses[ _-](.+?)(?:_[0-9a-f]{4,})?\.(?:json|csv)$/i;
const INTERFACE_NAME = /^Interfaces[ _-](.+?)(?:_[0-9a-f]{4,})?\.(?:txt|tsv|csv)$/i;
const OBJECT_NAMES: Partial<Record<FirewallFormatId, RegExp>> = {
  fortinet_address_group: /^(?:Adress(?:es)?[ _-]Groups?|Address[ _-]Groups?)[ _-](.+?)(?:_[0-9a-f]{4,})?\.(?:json|csv)$/i,
  fortinet_service_list: /^Services[ _-](.+?)(?:_[0-9a-f]{4,})?\.(?:json|csv)$/i,
  fortinet_service_group: /^Service[ _-]Groups?[ _-](.+?)(?:_[0-9a-f]{4,})?\.(?:json|csv)$/i,
  fortinet_vip_group: /^Virtual[ _-]IPs?[ _-]Groups?[ _-](.+?)(?:_[0-9a-f]{4,})?\.(?:json|csv)$/i,
  fortinet_vip_list: /^Virtual[ _-]IPs?[ _-](.+?)(?:_[0-9a-f]{4,})?\.(?:json|csv)$/i,
  fortinet_ip_pool: /^IP[ _-]Pools?[ _-](.+?)(?:_[0-9a-f]{4,})?\.(?:json|csv)$/i,
};

/** VDOM aus dem Dateinamen, je nach erkanntem Format. */
export function vdomFromFilename(filename: string, formatId: FirewallFormatId): string | null {
  const base = filename.split(/[\\/]/).pop() ?? filename;
  if (formatId === 'fortinet_policy_list') {
    const full = base.match(POLICY_FULL);
    if (full) return full[1];
    const short = base.match(POLICY_SHORT);
    return short ? short[1] : null;
  }
  if (formatId === 'fortinet_address_list') {
    const match = base.match(ADDRESS_NAME);
    return match ? match[1].trim() : null;
  }
  if (formatId === 'fortinet_interface_list') {
    const match = base.match(INTERFACE_NAME);
    return match ? match[1].trim() : null;
  }
  const objectPattern = OBJECT_NAMES[formatId];
  if (objectPattern) {
    const match = base.match(objectPattern);
    return match ? match[1].trim() : null;
  }
  return null;
}

/** VDOM aus der Spalte "Virtual Domain" der Interface-Liste (häufigster Wert). */
export function vdomFromInterfaceText(text: string): string | null {
  const header = parseInterfaceHeader(text);
  if (!header) return null;
  const column = header.indexOf('Virtual Domain');
  const counts = new Map<string, number>();
  const lines = text.replace(/^\uFEFF/, '').split(/\r?\n/).filter((line) => line.trim().length > 0);
  lines.slice(1).forEach((line) => {
    const cells = line.split('\t');
    const value = (cells[column] ?? '').trim();
    if (value) counts.set(value, (counts.get(value) ?? 0) + 1);
  });
  let best: string | null = null;
  let bestCount = 0;
  counts.forEach((count, value) => {
    if (count > bestCount) {
      best = value;
      bestCount = count;
    }
  });
  return best;
}

export interface FirewallFileDetection {
  formatId: FirewallFormatId;
  typ: FirewallUploadTyp;
  vdom: string | null;
}

export function detectFirewallUploadText(filename: string, text: string): FirewallFileDetection {
  const formatId = detectFirewallText(text, filename);
  let vdom = vdomFromFilename(filename, formatId);
  if (formatId === 'fortinet_interface_list') {
    vdom = vdomFromInterfaceText(text) ?? vdom;
  }
  return { formatId, typ: uploadTyp(formatId), vdom };
}

export async function detectFirewallFile(file: File): Promise<FirewallFileDetection> {
  const blob = file.size > 20 * 1024 * 1024 ? file.slice(0, 8000) : file;
  return detectFirewallUploadText(file.name, await blob.text());
}
